import React from 'react';
import { FaBox, FaImage, FaEye, FaTags } from 'react-icons/fa';
import { ProductFormPreviewProps } from './ProductFormTypes';

/**
 * Product Form Preview Component
 * Shows a live preview of how the product will appear
 */

const ProductFormPreview: React.FC<ProductFormPreviewProps> = ({
  product,
  optionTypes,
  selectedOptionTypes,
}) => {
  const hasBasicInfo = product.name || product.description || product.price;
  const hasImage = product.image_url && product.image_url.trim().length > 0;

  if (!hasBasicInfo) {
    return null;
  }

  // Get selected option type names
  const selectedOptionNames = selectedOptionTypes
    .map(id => optionTypes.find(ot => ot.id === id)?.display_name)
    .filter(Boolean);

  return (
    <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-5 rounded-xl border border-gray-200">
      <div className="flex items-center gap-2 mb-4">
        <FaEye className="text-gray-500" />
        <h3 className="text-lg font-semibold text-gray-900">Ürün Önizlemesi</h3>
      </div>

      <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-start gap-4">
          {/* Product Image or Icon */}
          <div className="flex-shrink-0">
            {hasImage ? (
              <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={product.image_url}
                  alt={product.name || 'Ürün resmi'}
                  className="w-full h-full object-cover"
                  onError={e => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg hidden items-center justify-center">
                  <FaBox className="text-white text-xl" />
                </div>
              </div>
            ) : (
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                <FaBox className="text-white text-xl" />
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className="flex-1 min-w-0">
            <div className="space-y-2">
              {/* Product Name */}
              <h4 className="font-semibold text-gray-900 text-lg leading-tight">
                {product.name || 'Ürün Adı'}
              </h4>

              {/* Product Description */}
              <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                {product.description || 'Ürün açıklaması...'}
              </p>

              {/* Price and Options Row */}
              <div className="flex items-center gap-4 pt-2">
                {/* Price */}
                <div className="flex items-center gap-1">
                  <span className="font-bold text-green-600 text-lg">
                    {product.price ? `${product.price} ₺` : '0 ₺'}
                  </span>
                </div>

                {/* Option Types */}
                {selectedOptionNames.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
                      <FaTags className="text-xs" />
                      {selectedOptionNames.length} Seçenek Türü
                    </span>
                  </div>
                )}
              </div>

              {/* Option Types Details */}
              {selectedOptionNames.length > 0 && (
                <div className="pt-2">
                  <p className="text-xs text-gray-500 mb-2">Mevcut seçenekler:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedOptionNames.map((name, index) => (
                      <span
                        key={index}
                        className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full font-medium"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Preview Footer */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-2">
              <span>Önizleme</span>
              {hasImage && (
                <div className="flex items-center gap-1">
                  <FaImage />
                  <span>Resimli</span>
                </div>
              )}
              {selectedOptionNames.length > 0 && (
                <div className="flex items-center gap-1">
                  <FaTags />
                  <span>Seçenekli</span>
                </div>
              )}
            </div>
            <span>Müşterilerin göreceği görünüm</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductFormPreview;
